import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import createAction from '@framework/state/Redux/createAction';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { AlertManager } from '@dealroadshow/uikit-alert';

interface ICallbacks<TPayload = {}, TMethodResponse = any> {
  responseHandler?: (response: TMethodResponse) => any,
  onSuccess?: (args: {
    response: TMethodResponse,
    payload: TPayload,
    dispatch: Function,
    getState: Function,
  }) => void,
  onError?: (args: {
    error: any,
    payload: TPayload,
    dispatch: Function,
    getState: Function,
  }) => void,
}

interface IOptions<TPayload extends Record<string, any>, TMethodResponse> {
  payload: TPayload,
  types: string[],
  method: (payload: TPayload, dispatch: Function, getState: Function) => Promise<TMethodResponse>,
  callbacks?: ICallbacks<TPayload, TMethodResponse>,
}

const defaultCallbacks: ICallbacks = {
  responseHandler: null,
  onSuccess: null,
  onError: null,
};

export const createAsyncRequest = <TPayload, TMethodResponse>(
  {
    payload = {} as TPayload,
    method,
    types: [REQUEST_TYPE, SUCCESS_TYPE, ERROR_TYPE],
    callbacks = defaultCallbacks,
  }: IOptions<TPayload, TMethodResponse>,
) => async (dispatch, getState) => {
  dispatch(createAction(REQUEST_TYPE));

  try {
    const response = await method(payload, dispatch, getState);

    if (callbacks.onSuccess) {
      callbacks.onSuccess({ response, payload, dispatch, getState });
    }

    dispatch(createAction(SUCCESS_TYPE, callbacks.responseHandler ? callbacks.responseHandler(response) : response));
  } catch (error) {
    if (Dispatcher.isAbortError(error)) {
      return;
    }

    if (callbacks.onError) {
      callbacks.onError({ error, payload, dispatch, getState });
    } else {
      AlertManager.error(getErrorMessage(error));
    }

    dispatch(createAction(ERROR_TYPE, error));
  }
};

export default createAsyncRequest;
